import { useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Bot,
  CheckCircle,
  CirclePlus,
  ExternalLink,
  Info,
  Loader2,
  PlayCircle,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getCertificateRequest,
  runCertificateRequestAutomation,
  updateCertificateRequestStatus,
  requestCertificateInfo,
  type CertificateRequestDetail,
  type CertificateRequestEventType,
  type CertificateRequestStatus,
} from "@/services/certificateRequestsService";
import {
  getIdDigitalCertificateUrl,
  type CertificateRequestCompanyType,
} from "@/pages/certificate-requests/idDigitalUrls";
import { CERTIFICATE_REQUESTS_QUERY_KEY } from "@/pages/certificate-requests/certificate-requests-page";

const STATUS_BADGE_VARIANT: Record<CertificateRequestStatus, StatusBadgeVariant> = {
  pending: "warning",
  in_progress: "info",
  fulfilled: "success",
  failed: "danger",
};

const EVENT_ICONS: Record<CertificateRequestEventType, JSX.Element> = {
  created: <CirclePlus className="h-4 w-4" />,
  status_changed: <RefreshCw className="h-4 w-4" />,
  info_requested: <Info className="h-4 w-4" />,
};

const EVENT_ICON_COLORS: Record<CertificateRequestEventType, string> = {
  created: "bg-muted text-muted-foreground",
  status_changed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  info_requested: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function fullName(req: CertificateRequestDetail): string {
  const parts = [
    req.firstName,
    req.secondName?.trim(),
    req.firstLastName,
    req.secondLastName?.trim(),
  ].filter(Boolean);
  return parts.join(" ");
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,160px)_1fr] gap-3 py-2 first:pt-0 last:pb-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

function TimelineEvent({
  event,
  isLast,
}: {
  event: CertificateRequestDetail["events"][number];
  isLast: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const eventType = event.eventType as CertificateRequestEventType;
  const icon = EVENT_ICONS[eventType] ?? <CirclePlus className="h-4 w-4" />;
  const iconColor = EVENT_ICON_COLORS[eventType] ?? "bg-muted text-muted-foreground";

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconColor}`}>
          {icon}
        </div>
        {!isLast && (
          <div className="mt-1 flex-1 border-l border-dashed border-border" />
        )}
      </div>
      <div className={`min-w-0 flex-1 space-y-1 ${isLast ? "" : "pb-5"}`}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground">
            {t(`certificateRequests.detail.event_${eventType}`)}
          </span>
          <span className="text-xs text-muted-foreground">
            {new Date(event.createdAt).toLocaleString()}
          </span>
        </div>
        {event.actorDisplayName && (
          <p className="text-xs text-muted-foreground">{event.actorDisplayName}</p>
        )}
        {event.message && (
          <blockquote className="mt-1.5 rounded-md border-l-2 border-border bg-muted/50 px-3 py-2">
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {event.message}
            </p>
          </blockquote>
        )}
      </div>
    </div>
  );
}

export function CertificateRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const [requestInfoMessage, setRequestInfoMessage] = useState("");
  const [isSubmittingInfo, setIsSubmittingInfo] = useState(false);

  const { data: request, isLoading, isError } = useQuery({
    queryKey: [...CERTIFICATE_REQUESTS_QUERY_KEY, "detail", id],
    queryFn: () => getCertificateRequest(id!),
    enabled: Boolean(id),
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CERTIFICATE_REQUESTS_QUERY_KEY });
  }, [queryClient]);

  const updateStatusMutation = useMutation({
    mutationFn: ({
      reqId,
      status,
    }: {
      reqId: string;
      status: CertificateRequestStatus;
    }) => updateCertificateRequestStatus(reqId, status),
    onSuccess: () => {
      invalidate();
      toast.success(t("certificateRequests.detail.statusUpdated"));
    },
    onError: () => {
      toast.error(t("certificateRequests.page.statusUpdateError"));
    },
  });

  const runAutomationMutation = useMutation({
    mutationFn: (reqId: string) => runCertificateRequestAutomation(reqId),
    onSuccess: () => {
      invalidate();
      toast.success(t("certificateRequests.page.automationSuccess"));
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      const message =
        err.response?.data?.message ??
        err.message ??
        t("certificateRequests.page.automationFailed");
      toast.error(message);
    },
  });

  const handleSubmitRequestInfo = useCallback(async () => {
    if (!id || !requestInfoMessage.trim()) return;
    setIsSubmittingInfo(true);
    try {
      await requestCertificateInfo(id, requestInfoMessage.trim());
      toast.success(t("certificateRequests.detail.requestInfoSent"));
      setRequestInfoOpen(false);
      setRequestInfoMessage("");
      invalidate();
    } catch {
      toast.error(t("certificateRequests.detail.requestInfoError"));
    } finally {
      setIsSubmittingInfo(false);
    }
  }, [id, requestInfoMessage, invalidate, t]);

  if (!id || isLoading || (request == null && !isError)) {
    return <LoadingPage />;
  }

  if (isError || request == null) {
    toast.error(t("certificateRequests.page.error"));
    navigate("/certificate-requests", { replace: true });
    return <LoadingPage />;
  }

  const idDigitalUrl = getIdDigitalCertificateUrl(
    request.companyType as CertificateRequestCompanyType,
    request.durationYears
  );
  const statusVariant = STATUS_BADGE_VARIANT[request.status];
  const canAct =
    request.status === "pending" || request.status === "in_progress";

  return (
    <div className="fx-content">
      <PageHeader
        title={`${t("certificateRequests.detail.title")} — ${request.tenantName || request.id}`}
        description={t("certificateRequests.detail.subtitle", {
          id: request.id.slice(0, 8),
        })}
        onBack={() => navigate("/certificate-requests")}
      />

      <div className="mt-6 space-y-4">
        {/* Failed: what went wrong */}
        {request.status === "failed" && request.notes && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-destructive">
                {t("certificateRequests.detail.whatWentWrong")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm text-foreground">
                {request.notes}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
          {/* Left column */}
          <div className="space-y-4">
            {/* Request summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t("certificateRequests.detail.requestSection")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border">
                  <DetailRow label={t("certificateRequests.detail.status")}>
                    <StatusBadge variant={statusVariant}>
                      {t(`certificateRequests.page.status${capitalize(request.status)}`)}
                    </StatusBadge>
                  </DetailRow>
                  <DetailRow label={t("certificateRequests.detail.companyType")}>
                    <span className="uppercase">{request.companyType}</span>
                  </DetailRow>
                  <DetailRow label={t("certificateRequests.detail.duration")}>
                    {request.durationYears}{" "}
                    {t("certificateRequests.page.years")}
                  </DetailRow>
                  <DetailRow label={t("certificateRequests.detail.date")}>
                    {formatDisplayDate(request.createdAt, {
                      includeTime: true,
                      locale: i18n.language,
                    })}
                  </DetailRow>
                  {request.idDigitalRequestNumber && (
                    <DetailRow
                      label={t("certificateRequests.detail.requestNumber")}
                    >
                      {request.idDigitalRequestNumber}
                    </DetailRow>
                  )}
                  {request.comprobantePdfUrl && (
                    <DetailRow
                      label={t(
                        "certificateRequests.detail.comprobanteDeSolicitud"
                      )}
                    >
                      <a
                        href={request.comprobantePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary underline underline-offset-4 transition-colors duration-150 hover:no-underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {t("certificateRequests.detail.downloadComprobante")}
                      </a>
                    </DetailRow>
                  )}
                </dl>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open(idDigitalUrl, "_blank", "noopener")
                    }
                    className="gap-2 transition-colors duration-150"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t("certificateRequests.detail.openIdDigital")}
                  </Button>
                  {canAct && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={runAutomationMutation.isPending}
                        onClick={() => runAutomationMutation.mutate(request.id)}
                        className="gap-2 transition-colors duration-150"
                      >
                        {runAutomationMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Bot className="h-4 w-4" />
                        )}
                        {t("certificateRequests.detail.runAutomation")}
                      </Button>
                      {request.status === "pending" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={updateStatusMutation.isPending}
                          onClick={() =>
                            updateStatusMutation.mutate({
                              reqId: request.id,
                              status: "in_progress",
                            })
                          }
                          className="gap-2 transition-colors duration-150"
                        >
                          <PlayCircle className="h-4 w-4" />
                          {t("certificateRequests.detail.markInProgress")}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={updateStatusMutation.isPending}
                        onClick={() =>
                          updateStatusMutation.mutate({
                            reqId: request.id,
                            status: "fulfilled",
                          })
                        }
                        className="gap-2 transition-colors duration-150"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {t("certificateRequests.detail.markFulfilled")}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Applicant */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t("certificateRequests.detail.applicantSection")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border">
                  <DetailRow label={t("certificateRequests.detail.applicant")}>
                    <span className="font-medium">{fullName(request)}</span>
                  </DetailRow>
                  <DetailRow label={t("certificateRequests.detail.email")}>
                    <a
                      href={`mailto:${request.email}`}
                      className="text-primary underline underline-offset-4 transition-colors duration-150 hover:no-underline"
                    >
                      {request.email}
                    </a>
                  </DetailRow>
                  {request.requesterDocumentType &&
                    request.requesterDocumentNumber && (
                      <DetailRow
                        label={t("certificateRequests.detail.document")}
                      >
                        {t(
                          `certificateRequests.detail.documentType_${request.requesterDocumentType}`
                        )}{" "}
                        {request.requesterDocumentNumber}
                      </DetailRow>
                    )}
                </dl>
              </CardContent>
            </Card>

            {/* Company */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {t("certificateRequests.detail.companySection")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border">
                  <DetailRow label={t("certificateRequests.detail.tenant")}>
                    <span className="font-medium">
                      {request.tenantName || "—"}
                    </span>
                  </DetailRow>
                  <DetailRow label={t("certificateRequests.detail.rut")}>
                    {request.tenantRut || "—"}
                  </DetailRow>
                  {request.tenantAddress && (
                    <DetailRow
                      label={t("certificateRequests.detail.companyAddress")}
                    >
                      {request.tenantAddress}
                    </DetailRow>
                  )}
                  {(request.tenantCity || request.tenantDepartment) && (
                    <DetailRow
                      label={t("certificateRequests.detail.companyCityDept")}
                    >
                      {[request.tenantCity, request.tenantDepartment]
                        .filter(Boolean)
                        .join(", ")}
                    </DetailRow>
                  )}
                  {request.tenantPhone && (
                    <DetailRow
                      label={t("certificateRequests.detail.companyPhone")}
                    >
                      {request.tenantPhone}
                    </DetailRow>
                  )}
                  {request.tenantContactEmail && (
                    <DetailRow
                      label={t("certificateRequests.detail.companyEmail")}
                    >
                      <a
                        href={`mailto:${request.tenantContactEmail}`}
                        className="text-primary underline underline-offset-4 transition-colors duration-150 hover:no-underline"
                      >
                        {request.tenantContactEmail}
                      </a>
                    </DetailRow>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>

          {/* Right column: activity history */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
              <CardTitle className="text-base">
                {t("certificateRequests.detail.historyTitle")}
              </CardTitle>
              {canAct && (
                <Button size="sm" onClick={() => setRequestInfoOpen(true)}>
                  {t("certificateRequests.detail.requestInfoButton")}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {request.events.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("certificateRequests.detail.historyEmpty")}
                </p>
              ) : (
                <div>
                  {request.events.map((event, index) => (
                    <TimelineEvent
                      key={event.id}
                      event={event}
                      isLast={index === request.events.length - 1}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Request more info dialog */}
      <Dialog open={requestInfoOpen} onOpenChange={setRequestInfoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("certificateRequests.detail.requestInfoTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cert-request-info-message">
              {t("certificateRequests.detail.requestInfoMessage")}
            </Label>
            <Textarea
              id="cert-request-info-message"
              placeholder={t(
                "certificateRequests.detail.requestInfoPlaceholder"
              )}
              value={requestInfoMessage}
              onChange={(e) => setRequestInfoMessage(e.target.value)}
              rows={4}
              disabled={isSubmittingInfo}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRequestInfoOpen(false)}
              disabled={isSubmittingInfo}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void handleSubmitRequestInfo()}
              disabled={!requestInfoMessage.trim() || isSubmittingInfo}
            >
              {isSubmittingInfo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("certificateRequests.detail.sending")}
                </>
              ) : (
                t("certificateRequests.detail.requestInfoSubmit")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function capitalize(str: string): string {
  if (!str) return str;
  return str
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}
